// Reusable phone-number display — the ONE way to render a phone anywhere in
// the app (customer page, facebook lead card, future CRM surfaces, ...).
//
// Rendering:
//   - Israeli numbers → national format ("052-118-7345" style), no flag.
//   - International numbers → international format + a country-flag emoji
//     derived from the parsed E.164 country (no deps, no network images).
//   - Unparseable input → rendered raw (still LTR + copyable).
//   - RTL-safe: the whole number cluster is dir="ltr" inside RTL pages.
//
// Interaction (always a copy affordance):
//   - Desktop (fine pointer): click copies the DISPLAYED text + snackbar
//     ("what you see is what you copy", same policy as CopyablePhone).
//   - Touch (coarse pointer): click opens a small popover with
//     העתקה / חיוג (tel:) / וואטסאפ (wa.me) actions.
//   - Click/keydown stopPropagation so it can live inside clickable rows/cards.
import { useState } from "react";
import {
  Box,
  Popover,
  Tooltip,
  MenuItem,
  MenuList,
  Typography,
  ListItemIcon,
  ListItemText,
  useMediaQuery
} from "@material-ui/core";
import { Check, ContentCopy, Phone as PhoneIcon, WhatsApp } from "@material-ui/icons";
import { useSnackbar } from "notistack5";
import { parsePhoneNumber, toInternationalFormat } from "utils/Haim/phoneNumbersUtils";
import { useCopyToClipboard } from "./_dashboard/common/useCopyToClipboard";
import type { SxProps } from "@material-ui/system";
import type { Theme } from "@material-ui/core/styles";

const REGIONAL_INDICATOR_BASE = 0x1f1e6; // 🇦

/** ISO 3166-1 alpha-2 → flag emoji (regional indicator pair). */
function countryFlagEmoji(iso2: string | undefined): string | null {
  if (!iso2 || !/^[A-Za-z]{2}$/.test(iso2)) return null;
  return String.fromCodePoint(
    ...iso2
      .toUpperCase()
      .split("")
      .map((char) => REGIONAL_INDICATOR_BASE + (char.charCodeAt(0) - 65))
  );
}

type PhoneNumberDisplayProps = {
  phone: string;
  /** "full" (default) = body2 sizing; "compact" = caption sizing for dense rows/chips. */
  variant?: "full" | "compact";
  sx?: SxProps<Theme>;
};

export default function PhoneNumberDisplay({
  phone,
  variant = "full",
  sx
}: PhoneNumberDisplayProps): JSX.Element | null {
  const { enqueueSnackbar } = useSnackbar();
  const { copied, copy } = useCopyToClipboard();
  // Touch-first devices get the action popover; pointer devices copy directly.
  const isTouch = useMediaQuery("(pointer: coarse)");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (!phone) return null;

  const parsed = parsePhoneNumber(phone);
  const isIsraeli = parsed?.country === "IL";
  const display = (isIsraeli ? parsed?.formatNational() : parsed?.formatInternational()) || phone;
  const flag = isIsraeli ? null : countryFlagEmoji(parsed?.country);
  const e164 = parsed?.number ?? toInternationalFormat(phone) ?? phone;
  const waDigits = String(e164).replace(/\D/g, "");

  const handleCopy = async (): Promise<void> => {
    const ok = await copy(display);
    enqueueSnackbar(ok ? "המספר הועתק" : "ההעתקה נכשלה", {
      variant: ok ? "success" : "error"
    });
  };

  const activate = (target: HTMLElement): void => {
    if (isTouch) setAnchorEl(target);
    else void handleCopy();
  };

  const compact = variant === "compact";
  const copyIconSize = compact ? 13 : 15;

  const trigger = (
    <Box
      role="button"
      tabIndex={0}
      aria-label={`מספר טלפון ${display}`}
      aria-haspopup={isTouch ? "true" : undefined}
      aria-expanded={isTouch ? Boolean(anchorEl) : undefined}
      onClick={(e) => {
        e.stopPropagation();
        activate(e.currentTarget);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          activate(e.currentTarget);
        }
      }}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: compact ? 0.5 : 0.75,
        direction: "ltr", // numbers stay LTR inside RTL pages
        cursor: "pointer",
        borderRadius: 0.5,
        px: 0.25,
        transition: "background-color 0.15s ease",
        "&:hover": { bgcolor: "action.hover" },
        "&:hover .copy-icon": { opacity: 0.85 },
        ...(sx as object)
      }}
    >
      {flag && (
        <Box component="span" aria-hidden sx={{ fontSize: compact ? 14 : 16, lineHeight: 1 }}>
          {flag}
        </Box>
      )}
      <Typography component="span" dir="ltr" noWrap variant={compact ? "caption" : "body2"}>
        {display}
      </Typography>
      {copied ? (
        <Check sx={{ fontSize: copyIconSize, color: "success.main" }} />
      ) : (
        <ContentCopy className="copy-icon" sx={{ fontSize: copyIconSize, opacity: 0.35 }} />
      )}
    </Box>
  );

  return (
    <>
      {isTouch ? (
        trigger
      ) : (
        <Tooltip title={copied ? "הועתק" : "העתקה"}>{trigger}</Tooltip>
      )}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MenuList dense sx={{ minWidth: 160 }}>
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              setAnchorEl(null);
              void handleCopy();
            }}
          >
            <ListItemIcon>
              <ContentCopy fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="העתקה" />
          </MenuItem>
          <MenuItem
            component="a"
            href={`tel:${e164}`}
            onClick={(e) => {
              e.stopPropagation();
              setAnchorEl(null);
            }}
          >
            <ListItemIcon>
              <PhoneIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="חיוג" />
          </MenuItem>
          <MenuItem
            component="a"
            href={`https://wa.me/${waDigits}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.stopPropagation();
              setAnchorEl(null);
            }}
          >
            <ListItemIcon>
              <WhatsApp fontSize="small" sx={{ color: "#25D366" }} />
            </ListItemIcon>
            <ListItemText primary="וואטסאפ" />
          </MenuItem>
        </MenuList>
      </Popover>
    </>
  );
}
